import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api.js';
import './stacks.css';

export interface EnvSummary {
  id: string;
  name: string;
  kind: 'production_pair_member' | 'standalone';
  status: string;
  deployedSha: string | null;
  isActiveProd: boolean;
  droplets: Array<{ id: string; name: string; ipv4: string | null; state: string }>;
}

export interface StackSummary {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  repoOwner: string | null;
  repoName: string | null;
  appType: 'node' | 'python' | null;
  repoSubdir: string | null;
  buildCommand: string | null;
  runCommand: string | null;
  migrateCommand: string | null;
  frontingMode: 'none' | 'nginx' | 'do_lb' | null;
  domain: string | null;
  tlsEmail: string | null;
  backupBucket: string | null;
  backupRegion: string | null;
  environments: EnvSummary[];
}

function statusClass(status: string): string {
  if (status === 'healthy') return 'ok';
  if (status === 'deploying') return 'warn';
  if (status === 'unprovisioned') return 'neutral';
  return 'err';
}

export function EnvBadge({ env }: { env: EnvSummary }) {
  return (
    <div className={`env-badge env-${env.name}`}>
      <span className="env-name mono">
        {env.name}
        {env.isActiveProd ? <span className="env-active">ACTIVE</span> : null}
      </span>
      <span className={`env-status status-${statusClass(env.status)}`}>{env.status}</span>
      <span className="env-sha mono">{env.deployedSha ? env.deployedSha.slice(0, 7) : '—'}</span>
      <span className="env-droplets">{env.droplets.length} droplet{env.droplets.length === 1 ? '' : 's'}</span>
    </div>
  );
}

export function StacksPage() {
  const queryClient = useQueryClient();
  const { data: stacks, isLoading, error } = useQuery<StackSummary[]>({
    queryKey: ['stacks'],
    queryFn: () => apiFetch('/stacks'),
  });
  const [showCreate, setShowCreate] = useState(false);

  const sync = useMutation({
    mutationFn: () => apiFetch<{ seen: number; missing: number }>('/stacks/sync', { method: 'POST' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['stacks'] }),
  });

  return (
    <div className="stacks-page">
      <header className="page-header">
        <div>
          <p className="page-eyebrow">Environments · Blue / Gold / Beta</p>
          <h1>Stacks</h1>
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost" onClick={() => sync.mutate()} disabled={sync.isPending}>
            {sync.isPending ? 'Syncing…' : 'Sync from DigitalOcean'}
          </button>
          <button className="btn btn-amber" onClick={() => setShowCreate(true)}>
            New stack
          </button>
        </div>
      </header>
      {sync.error ? <p className="error-text">{(sync.error as Error).message}</p> : null}
      {isLoading ? <p className="mono dim">loading…</p> : null}
      {error ? <p className="error-text">{(error as Error).message}</p> : null}

      <div className="stack-grid">
        {(stacks ?? []).map((stack) => (
          <Link key={stack.id} to={`/stacks/${stack.id}`} className="card stack-card">
            <div className="stack-card-head">
              <h2>{stack.name}</h2>
              <span className="mono dim">{stack.slug}</span>
            </div>
            {stack.repoOwner ? (
              <p className="mono stack-repo">
                {stack.repoOwner}/{stack.repoName}
              </p>
            ) : (
              <p className="dim stack-repo">no repository linked</p>
            )}
            <div className="stack-envs">
              {stack.environments.map((env) => (
                <EnvBadge key={env.id} env={env} />
              ))}
            </div>
          </Link>
        ))}
        {stacks && stacks.length === 0 ? (
          <div className="card stack-empty">
            <p>No stacks yet. Create one to start grouping droplets, a load balancer, and environments.</p>
          </div>
        ) : null}
      </div>

      {showCreate ? <CreateStackDialog onClose={() => setShowCreate(false)} /> : null}
    </div>
  );
}

function CreateStackDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [repoOwner, setRepoOwner] = useState('');
  const [repoName, setRepoName] = useState('');
  const [appType, setAppType] = useState<'node' | 'python'>('node');
  const [repoSubdir, setRepoSubdir] = useState('');
  const [buildCommand, setBuildCommand] = useState('');
  const [runCommand, setRunCommand] = useState('');
  const [migrateCommand, setMigrateCommand] = useState('');
  const [frontingMode, setFrontingMode] = useState<'none' | 'nginx' | 'do_lb'>('none');
  const [domain, setDomain] = useState('');
  const [tlsEmail, setTlsEmail] = useState('');
  const [backupBucket, setBackupBucket] = useState('');
  const [backupRegion, setBackupRegion] = useState('');
  const [error, setError] = useState('');

  const create = useMutation({
    mutationFn: () =>
      apiFetch('/stacks', {
        method: 'POST',
        body: {
          name,
          slug,
          appType,
          ...(repoOwner && repoName ? { repoOwner, repoName } : {}),
          ...(repoSubdir ? { repoSubdir } : {}),
          ...(buildCommand ? { buildCommand } : {}),
          ...(runCommand ? { runCommand } : {}),
          ...(migrateCommand ? { migrateCommand } : {}),
          frontingMode,
          ...(domain ? { domain } : {}),
          ...(tlsEmail ? { tlsEmail } : {}),
          ...(backupBucket ? { backupBucket } : {}),
          ...(backupRegion ? { backupRegion } : {}),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stacks'] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'failed to create stack'),
  });

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="card dialog" onClick={(e) => e.stopPropagation()}>
        <h2>New stack</h2>
        <p className="dim">
          Creates blue + gold production environments (shared prod DB, LB-switched) and a standalone
          beta environment. Droplets are tagged <span className="mono">stack:{slug || '<slug>'}</span>.
        </p>
        <div className="field">
          <label>Name</label>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slug || slug === slugify(name)) setSlug(slugify(e.target.value));
            }}
            autoFocus
          />
        </div>
        <div className="field">
          <label>Slug</label>
          <input className="mono" value={slug} onChange={(e) => setSlug(e.target.value)} />
        </div>
        <div className="dialog-row">
          <div className="field">
            <label>Repo owner</label>
            <input className="mono" value={repoOwner} onChange={(e) => setRepoOwner(e.target.value)} placeholder="org" />
          </div>
          <div className="field">
            <label>Repo name</label>
            <input className="mono" value={repoName} onChange={(e) => setRepoName(e.target.value)} placeholder="app" />
          </div>
        </div>
        <div className="field">
          <label>App type</label>
          <select className="dialog-select" value={appType} onChange={(e) => setAppType(e.target.value as 'node' | 'python')}>
            <option value="node">node</option>
            <option value="python">python</option>
          </select>
        </div>
        <div className="field">
          <label>Repo subdirectory (optional)</label>
          <input className="mono" value={repoSubdir} onChange={(e) => setRepoSubdir(e.target.value)} placeholder="e.g. backend" />
        </div>
        <div className="field">
          <label>Build command (optional)</label>
          <input className="mono" value={buildCommand} onChange={(e) => setBuildCommand(e.target.value)} placeholder="e.g. npm run build" />
        </div>
        <div className="field">
          <label>Run command (optional)</label>
          <input className="mono" value={runCommand} onChange={(e) => setRunCommand(e.target.value)} placeholder="e.g. node dist/server.js" />
          {appType === 'python' ? <p className="dim">Python apps require a run command.</p> : null}
        </div>
        <div className="field">
          <label>Migrate command (optional)</label>
          <input className="mono" value={migrateCommand} onChange={(e) => setMigrateCommand(e.target.value)} placeholder="e.g. python manage.py migrate" />
        </div>
        <div className="field">
          <label>Fronting mode</label>
          <select className="dialog-select" value={frontingMode} onChange={(e) => setFrontingMode(e.target.value as 'none' | 'nginx' | 'do_lb')}>
            <option value="none">none</option>
            <option value="nginx">nginx</option>
            <option value="do_lb">do_lb</option>
          </select>
          {frontingMode !== 'none' ? <p className="dim">nginx mode requires a domain + TLS email; do_lb terminates TLS at Cloudflare.</p> : null}
        </div>
        {frontingMode !== 'none' ? (
          <div className="field">
            <label>Domain (optional)</label>
            <input className="mono" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="e.g. example.com" />
          </div>
        ) : null}
        {frontingMode === 'nginx' ? (
          <div className="field">
            <label>TLS email (optional)</label>
            <input className="mono" value={tlsEmail} onChange={(e) => setTlsEmail(e.target.value)} placeholder="e.g. admin@example.com" />
          </div>
        ) : null}
        <div className="field">
          <label>Backup bucket (optional)</label>
          <input className="mono" value={backupBucket} onChange={(e) => setBackupBucket(e.target.value)} placeholder="e.g. my-backups-bucket" />
        </div>
        <div className="field">
          <label>Backup region (optional)</label>
          <input className="mono" value={backupRegion} onChange={(e) => setBackupRegion(e.target.value)} placeholder="e.g. nyc3" />
        </div>
        <p className="error-text">{error}</p>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-amber"
            disabled={!name || !/^[a-z0-9][a-z0-9-]*$/.test(slug) || create.isPending}
            onClick={() => create.mutate()}
          >
            Create stack
          </button>
        </div>
      </div>
    </div>
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}
